<script setup lang="ts">
/**
 * Выбор одного значения в панели под своим триггером: статус и исполнитель
 * меняются прямо в строке списка, группировка — прямо в панели экрана.
 *
 * Отдельно от `FilterChip`: тот отбирает по нескольким значениям и остаётся
 * открытым, пока их набирают. Здесь выбор один, панель закрывается сразу — и
 * триггер задаёт слот, потому что им бывает и чип, и пилюля статуса, и аватар.
 *
 * Своя панель, а не `AppPopover` из пакета: тот стоит на floating-vue, которого
 * в зависимостях трекера нет.
 */
import { onClickOutside, useEventListener } from '@vueuse/core'
import { Check } from 'lucide-vue-next'
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * С этого числа вариантов список перестаёт читаться глазами и его листают:
 * четыре десятка репозиториев — это прокрутка вместо выбора. Порог заодно
 * оставляет короткие списки (статусы, участники проекта) без лишнего поля.
 */
const SEARCH_THRESHOLD = 10

const props = withDefaults(
  defineProps<{
    options: { value: string; label: string }[]
    modelValue: string
    /** Что написать вместо списка, когда выбирать не из чего */
    emptyText?: string
    /** К какому краю триггера прижать панель: у правых колонок она уедет за экран */
    align?: 'left' | 'right'
    /** Поиск принудительно, даже если вариантов немного */
    searchable?: boolean
  }>(),
  { emptyText: '', align: 'left', searchable: false },
)

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const { t } = useI18n()

const root = ref<HTMLElement | null>(null)
const search = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
const query = ref('')
const cursor = ref(0)

const withSearch = computed(() => props.searchable || props.options.length >= SEARCH_THRESHOLD)

/** Подходящие варианты: подстрока в подписи, регистр не важен. */
const visible = computed(() => {
  const needle = query.value.trim().toLowerCase()

  if (!needle) return props.options

  return props.options.filter((option) => option.label.toLowerCase().includes(needle))
})

// Открыли — поле пустое и курсор на первой строке: прошлый набор к новому
// выбору отношения не имеет.
watch(isOpen, async (open) => {
  if (!open) return

  query.value = ''
  cursor.value = 0

  if (!withSearch.value) return

  await nextTick()
  search.value?.focus()
})

watch(query, () => {
  cursor.value = 0
})

onClickOutside(root, () => {
  isOpen.value = false
})

/** Стрелки водят по списку, Enter берёт подсвеченное — руки не уходят с клавиатуры. */
const onKey = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()

    const step = event.key === 'ArrowDown' ? 1 : -1
    const count = visible.value.length

    if (count) cursor.value = (cursor.value + step + count) % count

    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()

    const option = visible.value[cursor.value]

    if (option) pick(option.value)
  }
}

// Escape ловим на документе: открыли список, передумали и жмут Escape, глядя
// на таблицу, — фокус в этот момент чаще всего не в панели.
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape') isOpen.value = false
})

const pick = (value: string) => {
  isOpen.value = false

  if (value !== undefined) emit('update:modelValue', value)
}
</script>

<template>
  <div ref="root" class="ipick">
    <slot name="trigger" :is-open="isOpen" :toggle="() => (isOpen = !isOpen)" />

    <div v-if="isOpen" class="ipick__panel" :class="`ipick__panel--${align}`" role="listbox">
      <input
        v-if="withSearch && options.length"
        ref="search"
        v-model="query"
        type="text"
        class="ipick__search"
        :placeholder="t('toolbar.search')"
        :aria-label="t('toolbar.search')"
        @keydown="onKey"
      />

      <p v-if="!options.length" class="ipick__empty">{{ emptyText }}</p>
      <p v-else-if="!visible.length" class="ipick__empty">{{ t('toolbar.filters.empty') }}</p>

      <button
        v-for="(option, index) in visible"
        :key="option.value"
        type="button"
        class="ipick__option"
        :class="{ 'ipick__option--cursor': withSearch && index === cursor }"
        role="option"
        :aria-selected="option.value === modelValue"
        @click="pick(option.value)"
        @mousemove="cursor = index"
      >
        <Check v-if="option.value === modelValue" class="ipick__check" :size="14" />
        <span v-else class="ipick__check" aria-hidden="true" />
        <span class="ipick__label">
          <slot name="option" :option="option">{{ option.label }}</slot>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.ipick {
  position: relative;
}

.ipick__panel {
  position: absolute;
  top: calc(100% + 4px);
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 180px;
  /* Длинный путь репозитория растягивал панель до тех пор, пока она не уезжала
     за край окна: ширину держим здесь, а сами строки обрезаем многоточием. */
  max-width: min(320px, calc(100vw - 32px));
  max-height: 320px;
  padding: 4px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.ipick__panel--left {
  left: 0;
}

.ipick__panel--right {
  right: 0;
}

.ipick__option {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

.ipick__option:hover,
.ipick__option--cursor {
  background: var(--color-bg-muted);
}

/* Строка не растягивает панель: длинный путь обрезается, полный виден в title
   у чипа, который из него получится. */
.ipick__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Поле поиска липнет к верху: список прокручивают, а искать нужно всегда */
.ipick__search {
  position: sticky;
  top: 0;
  z-index: 1;
  flex-shrink: 0;
  height: 32px;
  margin-bottom: 4px;
  padding: 0 8px;
  border: none;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
}

.ipick__search:focus {
  outline: none;
  border-bottom-color: var(--color-primary);
}

/* Галочка держит своё место и когда её нет: иначе строки прыгают при выборе. */
.ipick__check {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: var(--color-primary);
}

.ipick__empty {
  margin: 0;
  padding: 8px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}
</style>
